import { Lock, Mail, UserPlus } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent } from '~/components/ui/card';
import { cn } from '~/lib/utils';
import { getPermissionLabel } from './access-control-utils';

type FallbackVariant =
    | 'blurred-text'
    | 'blurred-input'
    | 'blurred-image'
    | 'blurred-card'
    | 'minimal';

type AccessDeniedFallbackProps = {
    variant?: FallbackVariant;
    reason?: string;
    missingPermissions?: string[];
    showRequestAccess?: boolean;
    showContactAdmin?: boolean;
    onRequestAccess?: () => void;
    className?: string;
};

export const AccessDeniedFallback = ({
    variant = 'blurred-card',
    reason,
    missingPermissions,
    showRequestAccess = false,
    showContactAdmin = false,
    onRequestAccess,
    className,
}: AccessDeniedFallbackProps) => {
    const defaultMessage = 'Sie haben keinen Zugriff auf diese Daten';
    
    // Build the message based on available info
    let message = reason || defaultMessage;
    if (!reason && missingPermissions && missingPermissions.length > 0) {
        if (missingPermissions.length === 1) {
            message = `Ihnen fehlt die Berechtigung: ${getPermissionLabel(missingPermissions[0])}`;
        } else {
            message = 'Fehlende Berechtigungen';
        }
    }

    // Common overlay content
    const OverlayContent = () => (
        <div className="text-center p-4 max-w-sm">
            <Lock className="h-8 w-8 mx-auto mb-3 text-gray-400" />
            <p className="text-sm text-gray-600 mb-3">{message}</p>
            
            {/* Display missing permissions as badges */}
            {missingPermissions && missingPermissions.length > 1 && (
                <div className="flex flex-wrap justify-center gap-1 mb-4">
                    {missingPermissions.map((permission) => (
                        <Badge key={permission} variant="secondary" className="text-xs">
                            {getPermissionLabel(permission)}
                        </Badge>
                    ))}
                </div>
            )}
            
            {/* Action buttons */}
            <div className="flex flex-col gap-2 items-center">
                {showRequestAccess && onRequestAccess && (
                    <Button variant="outline" size="sm" onClick={onRequestAccess}>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Zugriff anfragen
                    </Button>
                )}
                {showContactAdmin && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        Wenden Sie sich an einen Administrator
                    </p>
                )}
            </div>
        </div>
    );

    if (variant === 'minimal') {
        return (
            <div className={cn("flex items-center gap-2 text-gray-500", className)}>
                <Lock className="h-4 w-4" />
                <span className="text-sm">{message}</span>
                {missingPermissions && missingPermissions.length > 0 && (
                    <div className="flex gap-1">
                        {missingPermissions.slice(0, 2).map((permission) => (
                            <Badge key={permission} variant="outline" className="text-xs">
                                {getPermissionLabel(permission)}
                            </Badge>
                        ))}
                        {missingPermissions.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                                +{missingPermissions.length - 2}
                            </Badge>
                        )}
                    </div>
                )}
            </div>
        );
    }

    if (variant === 'blurred-card') {
        return (
            <Card className={cn("relative overflow-hidden", className)}>
                <CardContent className="p-6">
                    {/* Blurred placeholder content */}
                    <div className="blur-sm select-none pointer-events-none opacity-50">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                        <div className="h-4 bg-gray-200 rounded w-1/2 mb-3" />
                        <div className="h-20 bg-gray-100 rounded mb-3" />
                        <div className="h-4 bg-gray-200 rounded w-2/3" />
                    </div>
                    {/* Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                        <OverlayContent />
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (variant === 'blurred-text') {
        return (
            <div className={cn("relative", className)}>
                <div className="blur-sm select-none pointer-events-none">
                    <p className="text-gray-500">
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                        Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
                    </p>
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                    <OverlayContent />
                </div>
            </div>
        );
    }

    if (variant === 'blurred-image') {
        return (
            <div className={cn("relative rounded-lg overflow-hidden", className)}>
                <div className="blur-sm select-none pointer-events-none opacity-50">
                    <div className="h-32 w-full bg-linear-to-br from-gray-200 to-gray-300" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                    <OverlayContent />
                </div>
            </div>
        );
    }

    // Default: blurred-input
    return (
        <div className={cn("relative p-4 border rounded-lg", className)}>
            <div className="blur-sm select-none pointer-events-none opacity-50">
                <div className="h-10 bg-gray-100 rounded" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
                <OverlayContent />
            </div>
        </div>
    );
};
